import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { observer } from 'mobx-react-lite';
import { createPortal } from 'react-dom';
import { useEffect, useMemo, useState } from 'react';
import { StatisticsIntervalTypeEnum } from '@app/common';
import { TabEnum } from '../../../../../models/enums/tab-enum';
import { useRootStore } from '../../../../../providers/root-store-provider';
import { DashboardTradesProfitChartAbsolute } from './absolute/dashboard-trades-profit-chart-absolute';
import { DashboardTradesProfitChartPercentage } from './percentage/dashboard-trades-profit-chart-percentage';
import { Section } from '../../../../../components/app/section/section';
import { TabsMenu } from '../../../../../components/app/tabs/menu/tabs-menu';
import { TabsPanel } from '../../../../../components/app/tabs/pane/tabs-panel';
import { ProfitPeriodSwitcher } from '../../../../../components/app/period-switcher/profit-period-switcher';
import { IconClose } from '../../../../../components/app/icons/close';
import { PageHeadline } from '../../../../../components/app/page-headline/page-headline';
import { NoData } from '../../../../../components/app/no-data/no-data';

export const DashboardTradesProfitChart: React.FC = observer(() => {
    const { t } = useTranslation();
    const rootStore = useRootStore();
    const { statisticStore, portfolioStore } = rootStore;
    const { profitStore } = statisticStore;
    const [isFullScreen, setIsFullScreen] = useState(false);

    const toggleFullScreen = () => {
        setIsFullScreen(!isFullScreen);
    };

    useEffect(() => {
        const handleKeyDown = (event: KeyboardEvent) => {
            if (event.key === 'Escape' && setIsFullScreen) {
                setIsFullScreen(false);
            }
        };

        if (isFullScreen) {
            document.addEventListener('keydown', handleKeyDown);
            document.body.style.overflow = 'hidden';
        } else {
            document.removeEventListener('keydown', handleKeyDown);
            document.body.style.overflow = '';
        }
    }, [isFullScreen, setIsFullScreen]);

    const handleChangeTab = async (tab: TabEnum) => {
        profitStore.setTab(tab);
        profitStore.filterSelected();
    };

    const tabsMenu = [
        {
            label: t('pages.index.graphTitle.totalValue'),
            value: TabEnum.Absolute,
        },
        {
            label: t('pages.index.graphTitle.percentagePerformance'),
            value: TabEnum.Percentage,
        },
    ];

    const memoProfitChart = useMemo(() => {
        return (
            <div>
                {statisticStore.type !== StatisticsIntervalTypeEnum.Today ? (
                    <>
                        <div className="vl:inline-flex vl:items-baseline">
                            <div className="max-vl:my-small">
                                <TabsMenu
                                    onChange={handleChangeTab}
                                    value={profitStore.tab}
                                    items={tabsMenu}
                                    disableBottomPadding
                                />
                            </div>
                        </div>
                        <TabsPanel value={TabEnum.Absolute} activeTab={profitStore.tab}>
                            <DashboardTradesProfitChartAbsolute
                                toggleFullScreen={toggleFullScreen}
                                isFullScreen={isFullScreen}
                            />
                        </TabsPanel>
                        <TabsPanel value={TabEnum.Percentage} activeTab={profitStore.tab}>
                            <DashboardTradesProfitChartPercentage
                                toggleFullScreen={toggleFullScreen}
                                isFullScreen={isFullScreen}
                            />
                        </TabsPanel>
                    </>
                ) : (
                    <NoData>{t('layout.tables.noDetailedData')}</NoData>
                )}
            </div>
        );
    }, [profitStore.tab, isFullScreen, statisticStore.type]);

    const renderFullScreenChart = () => (
        <div className="fixed left-0 top-0 z-[9999] h-screen w-screen bg-body p-small">
            <div
                className="absolute right-small top-small z-[1000] flex h-[4rem] w-[4rem] cursor-pointer items-center justify-center rounded-full bg-brand-secondary/20 transition-all hover:bg-brand-secondary [&_i]:text-text-inverse"
                onClick={toggleFullScreen}
            >
                <IconClose />
            </div>
            <div className="vl:pr-[6rem]">
                <PageHeadline
                    title={t('pages.index.graphHeadline')}
                    mobileBreakUnder={'xl'}
                    action={<ProfitPeriodSwitcher />}
                />
            </div>
            {memoProfitChart}
        </div>
    );

    return (
        <>
            <Section
                title={t('pages.index.graphHeadline')}
                action={portfolioStore.selectedPortfolio.numberOfAssets === 0 ? <></> : <ProfitPeriodSwitcher />}
            >
                {memoProfitChart}
            </Section>
            {isFullScreen && createPortal(renderFullScreenChart(), document.body)}
        </>
    );
});
